// ./src/components/features/Inventory/PurchaseOrdersList/PurchaseOrdersList.tsx
//
// Responsive list of purchase orders — a bordered table on desktop, swipeable cards
// on phones (mirrors InventoryItemsList / the em-scaffold-ui list pattern). Row
// actions (edit / remove) are rights-gated by the record's `rights` array; the list
// owns the bottom LoadMoreButton and the empty / no-match states.

import React, { useCallback, useMemo } from "react";
import { Box, Table, Paper, Stack, Group, Text, Badge, ActionIcon, Tooltip, Checkbox, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  PurchaseOrder,
  PURCHASE_ORDER_STATUS_LABELS,
  PURCHASE_ORDER_STATUS_COLORS,
} from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment, useOptionalPurchaseOrdersContext } from "@/contexts";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonLines, CardSkeletonCheckbox, countCardActions, type CardSkeletonShape } from "@/components/generic";

import { formatMoney } from "../inventoryHelpers";

export interface PurchaseOrdersListProps {
  data?: PurchaseOrder[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEdit?: (po: PurchaseOrder) => void;
  onDelete?: (po: PurchaseOrder) => void;
  onPrint?: (po: PurchaseOrder) => void;
  onBuyList?: (po: PurchaseOrder) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAdd?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  hasQueryNarrowing?: boolean;
}

const lineTotal = (po: PurchaseOrder): number =>
  (po.items ?? []).reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0), 0);

const itemCount = (po: PurchaseOrder): number => (po.items ?? []).length;

const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <Badge variant="light" color={PURCHASE_ORDER_STATUS_COLORS[status || "draft"] || "gray"}>
    {PURCHASE_ORDER_STATUS_LABELS[status || "draft"] || status}
  </Badge>
);

const vendorName = (po: PurchaseOrder): string => po.vendor?.name || po.vendorLabel || "—";

interface RowProps {
  po: PurchaseOrder;
  onEdit?: (po: PurchaseOrder) => void;
  onDelete?: (po: PurchaseOrder) => void;
  onPrint?: (po: PurchaseOrder) => void;
  onBuyList?: (po: PurchaseOrder) => void;
  isDeleting: boolean;
  highlighted: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
  selectionColumn: boolean;
}

const RowActions: React.FC<{ po: PurchaseOrder; onEdit?: (po: PurchaseOrder) => void; onDelete?: (po: PurchaseOrder) => void; onPrint?: (po: PurchaseOrder) => void; onBuyList?: (po: PurchaseOrder) => void; isDeleting: boolean }> = ({ po, onEdit, onDelete, onPrint, onBuyList, isDeleting }) => {
  const { t } = useTranslation(["common", "inventory"]);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {onBuyList && recordHasRight(po, "edit") && (
        <Tooltip label={t("inventory:po.buyList.action", { defaultValue: "Buy list" })} withArrow position="top">
          <ActionIcon variant="subtle" onClick={() => onBuyList(po)} aria-label={t("inventory:po.buyList.action", { defaultValue: "Buy list" })}>
            <AppIcon iconName="ClipboardCheck" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onPrint && (
        <Tooltip label={t("common:buttons.print", { defaultValue: "Print" })} withArrow position="top">
          <ActionIcon variant="subtle" onClick={() => onPrint(po)} aria-label={t("common:buttons.print", { defaultValue: "Print" })}>
            <AppIcon iconName="Printer" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onEdit && recordHasRight(po, "edit") && (
        <Tooltip label={t("common:buttons.edit", { defaultValue: "Edit" })} withArrow position="top">
          <ActionIcon variant="subtle" onClick={() => onEdit(po)} aria-label={t("common:buttons.edit", { defaultValue: "Edit" })}>
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && recordHasRight(po, "remove") && (
        <Tooltip label={t("common:buttons.remove", { defaultValue: "Remove" })} withArrow position="top">
          <ActionIcon variant="subtle" color="red" loading={isDeleting} onClick={() => onDelete(po)} aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}>
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

const POTableRow: React.FC<RowProps> = ({ po, onEdit, onDelete, onPrint, onBuyList, isDeleting, highlighted, selected, selectionColumn, onToggleSelect }) => {
  const { t } = useTranslation(["inventory"]);

  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          <Checkbox checked={selected} onChange={() => po.id && onToggleSelect?.(po.id)} disabled={isDeleting} aria-label={po.code || ""} />
        </Table.Td>
      )}
      <Table.Td>
        <Text size="md" fw={600}>
          {po.code}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="md">{vendorName(po)}</Text>
        {po.event?.name ? (
          <Text size="sm" c="dimmed">
            {t("inventory:po.list.forEvent", { defaultValue: "For {{e}}", e: po.event.name })}
          </Text>
        ) : null}
      </Table.Td>
      <Table.Td style={{ width: 130 }}>
        <StatusBadge status={po.poStatus} />
      </Table.Td>
      <Table.Td style={{ width: 80 }} ta="right">
        <Text size="md">{itemCount(po)}</Text>
      </Table.Td>
      <Table.Td style={{ width: 130 }} ta="right">
        <Text size="md">{formatMoney(lineTotal(po), po.currencyCode)}</Text>
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <Text size="md">{formatDate(po.expectedAt)}</Text>
      </Table.Td>
      <Table.Td style={{ width: 130 }}>
        <RowActions po={po} onEdit={onEdit} onDelete={onDelete} onPrint={onPrint} onBuyList={onBuyList} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const POTableRowSkeleton: React.FC<{ selectionColumn: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={18} width={18} />
      </Table.Td>
    )}
    <Table.Td>
      <Skeleton height={16} width={90} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="60%" maw={200} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={20} width={72} />
    </Table.Td>
    <Table.Td style={{ width: 80 }}>
      <Skeleton height={16} width={24} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={16} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={16} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 100 }} />
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const POCardComponent: React.FC<CardProps> = ({
  po,
  onEdit,
  onDelete,
  onPrint,
  onBuyList,
  isDeleting,
  highlighted,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["inventory", "common"]);

  const toggleSelect = () => po.id && onToggleSelect?.(po.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(po, "edit")) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit"), color: "blue", onClick: () => onEdit(po), disabled: isDeleting });
    }

    if (onBuyList && recordHasRight(po, "edit")) {
      actions.push({ key: "buylist", icon: "ClipboardCheck", label: t("inventory:po.buyList.action", { defaultValue: "Buy list" }), color: "green", onClick: () => onBuyList(po), disabled: isDeleting });
    }

    if (onPrint) {
      actions.push({ key: "print", icon: "Printer", label: t("common:buttons.print", { defaultValue: "Print" }), color: "gray", onClick: () => onPrint(po), disabled: isDeleting });
    }

    if (onDelete && recordHasRight(po, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete"), color: "red", onClick: () => onDelete(po), loading: isDeleting, disabled: isDeleting });
    }

    return actions;
  }, [onEdit, onDelete, onPrint, onBuyList, po, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && <Checkbox checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={po.code || ""} />}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={700}>
              {po.code}
            </Text>
            <StatusBadge status={po.poStatus} />
          </Group>
          <Text size="md" c="neutral.7" lineClamp={1}>
            {vendorName(po)}
          </Text>
          <Text size="sm" c="dimmed">
            {t("inventory:po.list.cardMeta", {
              defaultValue: "{{n}} items · {{total}} · exp {{date}}",
              n: itemCount(po),
              total: formatMoney(lineTotal(po), po.currencyCode),
              date: formatDate(po.expectedAt),
            })}
          </Text>
        </Box>
        {showActions && <RowActions po={po} onEdit={onEdit} onDelete={onDelete} onPrint={onPrint} onBuyList={onBuyList} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const POCard = React.memo(POCardComponent);

const POCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <CardSkeletonLines count={3} sizes={["md", "md", "sm"]} gap={0} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const PurchaseOrdersListComponent: React.FC<PurchaseOrdersListProps> = ({
  data,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEdit,
  onDelete,
  onPrint,
  onBuyList,
  isRemoving: isRemovingProp,
  highlightedId,
  onAdd,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["inventory", "common"]);
  const { isDesktop } = useAppEnvironment();

  const ctx = useOptionalPurchaseOrdersContext();
  const orders = data ?? ctx?.purchaseOrders ?? [];
  const loading = loadingProp ?? ctx?.loading ?? false;
  const loadingMore = loadingMoreProp ?? ctx?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? ctx?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? ctx?.loadMore;
  const isRemoving = isRemovingProp ?? ctx?.isRemoving ?? (() => false);

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onBuyList, onPrint, onEdit, onDelete);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (po: PurchaseOrder) => {
      closeAllCards();
      onEdit?.(po);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (po: PurchaseOrder) => {
      closeAllCards();
      onDelete?.(po);
    },
    [onDelete, closeAllCards]
  );

  const handlePrint = useCallback(
    (po: PurchaseOrder) => {
      closeAllCards();
      onPrint?.(po);
    },
    [onPrint, closeAllCards]
  );

  const handleBuyList = useCallback(
    (po: PurchaseOrder) => {
      closeAllCards();
      onBuyList?.(po);
    },
    [onBuyList, closeAllCards]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = ctx?.isLoaded ?? data !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && orders.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("inventory:po.list.noMatch", { defaultValue: "No purchase orders match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("inventory:po.list.empty.title", { defaultValue: "No purchase orders yet" })}
          description={t("inventory:po.list.empty.description", {
            defaultValue: "Create an order to a vendor with the items you need to restock.",
          })}
          iconName="ClipboardList"
          actionLabel={onAdd ? t("inventory:po.list.empty.action", { defaultValue: "New purchase order" }) : undefined}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (po: PurchaseOrder): boolean =>
    isSelectable && ((!!onEdit && recordHasRight(po, "edit")) || (!!onDelete && recordHasRight(po, "remove")));

  const rowProps = (po: PurchaseOrder): RowProps => ({
    po,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    onPrint: onPrint ? handlePrint : undefined,
    onBuyList: onBuyList ? handleBuyList : undefined,
    isDeleting: isRemoving(po.id),
    highlighted: po.id === highlightedId,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(po),
    selected: selection?.isSelected(po.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = orders.filter(rowIsSelectable).map((po) => po.id).filter((id): id is string => !!id);

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("inventory:po.list.columns.code", { defaultValue: "PO #" })}</Table.Th>
                <Table.Th>{t("inventory:po.list.columns.vendor", { defaultValue: "Vendor" })}</Table.Th>
                <Table.Th style={{ width: 130 }}>{t("inventory:po.list.columns.status", { defaultValue: "Status" })}</Table.Th>
                <Table.Th style={{ width: 80 }} ta="right">
                  {t("inventory:po.list.columns.items", { defaultValue: "Items" })}
                </Table.Th>
                <Table.Th style={{ width: 130 }} ta="right">
                  {t("inventory:po.list.columns.total", { defaultValue: "Total" })}
                </Table.Th>
                <Table.Th style={{ width: 140 }}>{t("inventory:po.list.columns.expected", { defaultValue: "Expected" })}</Table.Th>
                <Table.Th style={{ width: 130 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <POTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : orders.map((po) => <POTableRow key={po.id} {...rowProps(po)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <POCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {orders.map((po) => (
          <POCard
            key={po.id}
            {...rowProps(po)}
            forceClose={shouldForceClose(po.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(po.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(po.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const PurchaseOrdersList = React.memo(PurchaseOrdersListComponent);

export default PurchaseOrdersList;
